/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';

type Props = {
  isSelected: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  color?: 'neutral' | 'gold';
};

const RatingStar = ({ isSelected, isDisabled, isReadOnly, color }: Props) => {
  const selectionState = isSelected ? 'selected' : 'unselected';
  const disabledState = isDisabled && 'Disabled';
  const readonlyState = isReadOnly && 'Readonly';
  const styleStatePrefix = `${selectionState}${disabledState || readonlyState || ''}`;
  const innerStyleClass =
    ratingStarStyles[`${styleStatePrefix}Color` as keyof typeof ratingStarStyles];
  const outerStyleClass =
    ratingStarStyles[`${styleStatePrefix}BorderColor` as keyof typeof ratingStarStyles];

  return (
    <svg
      viewBox="0 0 36 36"
      height="100%"
      width="100%"
      class={classNames([ratingStarStyles.base, `oj-c-rating-gauge-${color}`])}>
      <g>
        <path
          class={innerStyleClass}
          d="m18 1 5.0061 11.9524 12.9939 1.0344-9.9 8.4215 3.0246 12.5917-11.1246-6.7476-11.12461 6.7476 3.02461-12.5917-9.9-8.4215 12.9939-1.0344z"
        />
        <path
          class={outerStyleClass}
          d="m23.0061 12.9524-5.0061-11.9524-5.0061 11.9524-12.9939 1.0344 9.9 8.4215-3.02461 12.5917 11.12461-6.7476 11.1246 6.7476-3.0246-12.5917 9.9-8.4215zm10.5043 1.8394-8.5262 7.2528 2.6077 10.8562-9.5919-5.818-9.59192 5.818 2.60772-10.8562-8.52615-7.2528 11.19115-.891 4.3192-10.31227 4.3192 10.31227z"
        />
      </g>
    </svg>
  );
};

const ratingStarStyles = {
  base: cssProps`${{
    position: 'absolute'
  }}`,
  selectedColor: cssProps`${{
    fill: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-color-selected)'
  }}`,
  selectedBorderColor: cssProps`${{
    fill: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-border-color-selected)'
  }}`,
  unselectedColor: cssProps`${{
    fill: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-color-unselected)'
  }}`,
  unselectedBorderColor: cssProps`${{
    fill: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-border-color-unselected)'
  }}`,
  selectedReadonlyColor: cssProps`${{
    fill: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-color-selected-readonly)'
  }}`,
  selectedReadonlyBorderColor: cssProps`${{
    fill: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-border-color-selected-readonly)'
  }}`,
  unselectedReadonlyColor: cssProps`${{
    fill: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-color-unselected-readonly)'
  }}`,
  unselectedReadonlyBorderColor: cssProps`${{
    fill: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-border-color-unselected-readonly)'
  }}`,
  selectedDisabledColor: cssProps`${{
    fill: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-color-selected-disabled)'
  }}`,
  unselectedDisabledColor: cssProps`${{
    fill: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-color-unselected-disabled)'
  }}`,
  selectedDisabledBorderColor: cssProps`${{
    fill: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-border-color-selected-disabled)'
  }}`,
  unselectedDisabledBorderColor: cssProps`${{
    fill: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-border-color-unselected-disabled)'
  }}`
};
export { RatingStar };
